import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import formatCurrency from "@tvg/formatter/currency";
import { animated, config, useTransition } from "@react-spring/web";
import { Scroll, Paragraph } from "@tvg/design-system/web";
import { Button, Container, List, ListItem } from "./styled-components";

export interface DropdownProps {
  onSelectValue: (value: string) => void;
  values: Array<string>;
  selectedValue: string;
  isVisible: boolean;
  onClickAway: () => void;
}

const AnimatedContainer = animated(Container);
const DROPDOWN_HEIGHT = 294;
const DROPDOWN_HEIGHT_SPACING = 8;
const DROPDOWN_WIDTH = 170;

const Dropdown = ({
  onSelectValue,
  values,
  selectedValue,
  isVisible,
  onClickAway
}: DropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const transition = useTransition(isVisible, {
    config: isVisible ? { ...config.stiff } : { duration: 150 },
    from: { opacity: 0, transform: `translate3d(0px, 20px, 0px)` },
    enter: { opacity: 1, transform: `translate3d(0px, 0px, 0px)` },
    leave: { opacity: 0, transform: `translate3d(0px, 20px, 0px)` }
  });

  useEffect(() => {
    const button = document.getElementById("bet-slip-amount-selector-button");
    if (button) {
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - DROPDOWN_HEIGHT - DROPDOWN_HEIGHT_SPACING,
        left: rect.right - DROPDOWN_WIDTH
      });
    }
  }, [isVisible]);

  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isSelectorButton =
        target.getAttribute("data-qa-label") ===
        "bet-slip-amount-selector-button";
      const isBonusFundsToast = target.closest(
        '[data-qa-label="bonus-funds-toast"]'
      );

      if (
        ref.current &&
        !ref.current.contains(target) &&
        !isSelectorButton &&
        !isBonusFundsToast
      ) {
        onClickAway();
      }
    };
    document.addEventListener("click", handleClickAway, true);
    return () => {
      document.removeEventListener("click", handleClickAway, true);
    };
  }, [onClickAway]);

  useEffect(() => {
    const updatePosition = () => {
      const button = document.getElementById("bet-slip-amount-selector-button");
      if (button) {
        const rect = button.getBoundingClientRect();
        setDropdownPosition({
          top: rect.top - DROPDOWN_HEIGHT - DROPDOWN_HEIGHT_SPACING,
          left: rect.right - DROPDOWN_WIDTH
        });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, []);

  const dropdownContent = transition(
    (styles, isOpen) =>
      isOpen &&
      values.length > 0 && (
        <AnimatedContainer
          data-qa-label="bet-slip-amount-selector-dropdown"
          ref={ref}
          style={{
            ...styles,
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${DROPDOWN_WIDTH}px`
          }}
        >
          <Scroll
            height="294px"
            width="100%"
            overflowY="auto"
            overflowX="hidden"
            showBorderLeft
            hasShadow
          >
            <List>
              {values.map((item, index) => (
                <ListItem key={item}>
                  <Button
                    onClick={() => onSelectValue(item)}
                    data-qa-label={`dropdown-button-${item}`}
                    id={`dropdown-button-${item}`}
                    isActive={selectedValue === item}
                  >
                    <Paragraph
                      fontSize="s"
                      color="grey.900"
                      qaLabel="dropdown-button-text"
                    >
                      {index !== values.length - 1 && !Number.isNaN(item)
                        ? formatCurrency(+item)
                        : item}
                    </Paragraph>
                  </Button>
                </ListItem>
              ))}
            </List>
          </Scroll>
        </AnimatedContainer>
      )
  );

  return ReactDOM.createPortal(dropdownContent, document.body);
};

export default Dropdown;
